import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Activity, BarChart3, Bell, BookOpen, Briefcase, Building2, FolderLock,
    Gauge, Gift, LayoutDashboard, LineChart, LogOut, Menu, MessageSquare,
    Settings, UserRound, X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import { getUnreadCount } from '@/utils/unread';

// Same icon set as the desktop Sidebar — all lucide, so the two navs draw the
// same glyph at the same weight and both follow the active colour.
type NavItem = {
    labelKey: string;
    path?: string;
    icon: React.ElementType;
};

type MobileNavProps = {
    onLogout?: () => void;
};

// Mirror the desktop Sidebar's view resolution.
function getServiceMode(): 'accounting' | 'consulting' {
    const explicit = localStorage.getItem('activeServiceView');
    if (explicit === 'accounting' || explicit === 'consulting') return explicit;
    const ss = localStorage.getItem('setupService');
    return (ss === 'operate' || ss === 'incorporate') ? 'accounting' : 'consulting';
}

const navComms: NavItem[] = [
    { labelKey: "sidebar.notifications", path: "/notifications", icon: Bell },
    { labelKey: "sidebar.messageAdmin", path: "/message-admin", icon: MessageSquare },
];

export default function MobileNav({ onLogout }: MobileNavProps) {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [mode, setMode] = useState<'accounting' | 'consulting'>(getServiceMode);
    useEffect(() => {
        const update = () => setMode(getServiceMode());
        window.addEventListener('storage', update);
        window.addEventListener('serviceViewChanged', update);
        return () => {
            window.removeEventListener('storage', update);
            window.removeEventListener('serviceViewChanged', update);
        };
    }, []);
    const isAccounting = mode === 'accounting';

    const [unread, setUnread] = useState(getUnreadCount());
    useEffect(() => {
        const upd = () => setUnread(getUnreadCount());
        window.addEventListener('connct-unread', upd);
        window.addEventListener('storage', upd);
        return () => { window.removeEventListener('connct-unread', upd); window.removeEventListener('storage', upd); };
    }, []);

    const navPrimary: NavItem[] = [
        { labelKey: "sidebar.dashboard", path: "/dashboard", icon: LayoutDashboard },
        { labelKey: "sidebar.company", path: "/company-profile", icon: Building2 },
        { labelKey: "sidebar.profile", path: "/profile", icon: UserRound },
        { labelKey: "sidebar.dataRoom", path: isAccounting ? "/accounting/data-room" : "/data-room", icon: FolderLock },
        { labelKey: "sidebar.articles", path: "/articles", icon: BookOpen },
    ];

    const navAnalytics: NavItem[] = isAccounting
        ? [
            { labelKey: "sidebar.financialHealth", path: "/financial-health-check", icon: Activity },
        ]
        : [
            { labelKey: "sidebar.irScore", path: "/ir-score", icon: Gauge },
            { labelKey: "sidebar.financials", path: "/financials", icon: LineChart },
            { labelKey: "sidebar.kpis", path: "/kpis", icon: BarChart3 },
        ];

    const navServices: NavItem[] = isAccounting
        ? [
            { labelKey: "sidebar.services", path: "/services", icon: Briefcase },
            { labelKey: "sidebar.referAndEarn", path: "/refer-and-earn", icon: Gift },
            { labelKey: "sidebar.settings", path: "/settings", icon: Settings },
        ]
        : [
            { labelKey: "sidebar.referAndEarn", path: "/refer-and-earn", icon: Gift },
            { labelKey: "sidebar.settings", path: "/settings", icon: Settings },
        ];

    const handleNavigation = (path?: string) => {
        if (path) {
            navigate(path);
            setIsOpen(false);
        }
    };

    // Nested routes keep their section highlighted, as on the desktop Sidebar.
    const isActive = (path?: string) => {
        if (!path) return false;
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const renderItem = (item: NavItem) => {
        const active = isActive(item.path);
        const Icon = item.icon;
        return (
            <li key={item.labelKey}>
                <button
                    onClick={() => handleNavigation(item.path)}
                    disabled={!item.path}
                    aria-current={active ? 'page' : undefined}
                    className={`group w-full text-start px-3 py-3 rounded-lg text-sm flex items-center gap-3 transition-colors ${active
                        ? "acc-soft-bg acc-text font-semibold"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                >
                    <Icon
                        className={`w-[18px] h-[18px] shrink-0 transition-colors ${active ? "" : "text-gray-400 group-hover:text-gray-600"}`}
                        strokeWidth={active ? 2.2 : 1.75}
                    />
                    <span className="truncate">{t(item.labelKey)}</span>
                    {item.labelKey === 'sidebar.notifications' && unread > 0 && (
                        <span className="ms-auto min-w-[18px] h-[18px] px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">{unread > 9 ? '9+' : unread}</span>
                    )}
                </button>
            </li>
        );
    };

    return (
        <div className="md:hidden bg-white border-b border-gray-200 sticky top-0 z-30">
            <div className="flex items-center justify-between px-4 py-3">
                {/* Logo */}
                <Logo className="h-8" to="/dashboard" />

                {/* Hamburger */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                    {isOpen ? <X size={24} /> : <Menu size={24} />}
                    {!isOpen && unread > 0 && (
                        <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                    )}
                </button>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="border-t border-gray-100 bg-white absolute w-full left-0 shadow-lg max-h-[80vh] overflow-y-auto z-40">
                    <nav className="p-3 space-y-5">
                        {/* Primary Navigation */}
                        <ul className="space-y-1">
                            {navPrimary.map(renderItem)}
                        </ul>

                        {/* Analytics Section */}
                        <div>
                            <h3 className="px-3 text-[11px] font-semibold tracking-[0.09em] text-gray-400 uppercase mb-1.5">Analytics</h3>
                            <ul className="space-y-1">
                                {navAnalytics.map(renderItem)}
                            </ul>
                        </div>

                        {/* Communications Section */}
                        <div>
                            <h3 className="px-3 text-[11px] font-semibold tracking-[0.09em] text-gray-400 uppercase mb-1.5">Communications</h3>
                            <ul className="space-y-1">
                                {navComms.map(renderItem)}
                            </ul>
                        </div>

                        {/* Services Section */}
                        <div>
                            <ul className="space-y-1">
                                {navServices.map(renderItem)}
                            </ul>
                        </div>

                        {/* Sign Out */}
                        <div className="border-t border-gray-100 pt-4">
                            <button
                                onClick={() => {
                                    if (onLogout) onLogout();
                                    setIsOpen(false);
                                }}
                                className="w-full text-start px-3 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-3 transition-colors"
                            >
                                <LogOut className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
                                <span>{t('sidebar.signOut')}</span>
                            </button>
                        </div>
                    </nav>
                </div>
            )}
        </div>
    );
}
